import React from "react";
import Tooltip from "@mui/material/Tooltip";

// Colourful icon tiles for the document-viewer action bar (Info, Client
// Unknown, OCR, Approve, Reject, Link, Edit PDF, PDF Details, Close).
// Presentation only: every tile receives the caller's own onClick / disabled
// state, so behaviour is exactly what each action already did.
const TONES = {
  info: "bg-sky-100 text-sky-600 border-sky-200 hover:bg-sky-200",
  client: "bg-blue-500 text-white border-blue-500 hover:bg-blue-600",
  ocr: "bg-nbl-primary text-white border-nbl-primary hover:bg-nbl-hover",
  // OCR already completed (still clickable for Re-OCR)
  ocrDone: "bg-orange-600 text-white border-orange-600 hover:bg-orange-700",
  approve: "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600",
  reject: "bg-red-500 text-white border-red-500 hover:bg-red-600",
  link: "bg-cyan-100 text-cyan-700 border-cyan-200 hover:bg-cyan-200",
  // Separate Documents: soft baby pink with a magenta icon
  separate: "bg-[#f9d5e8] text-[#b0307a] border-[#f1b8d6] hover:bg-[#f4c2dc]",
  edit: "bg-amber-400 text-white border-amber-400 hover:bg-amber-500",
  details: "bg-violet-500 text-white border-violet-500 hover:bg-violet-600",
  // Neutral "close viewer" tile - light, so it doesn't read as Reject/Delete
  close: "bg-white text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-800",
  // Read-only status tiles (e.g. already Approved / Rejected)
  approvedStatus: "bg-emerald-50 text-emerald-600 border-emerald-200",
  rejectedStatus: "bg-red-50 text-red-600 border-red-200",
};

const DISABLED = "bg-gray-100 text-gray-300 border-gray-200";

const ViewerActionButton = ({
  tone,
  icon: Icon,
  title,
  disabled = false,
  disabledReason,
  onClick,
  loading = false,
  readOnly = false,
  className = "",
}) => {
  const inactive = disabled || loading;
  const tooltip = disabled && disabledReason ? disabledReason : title;

  return (
    <Tooltip title={tooltip} arrow placement="bottom" enterDelay={150}>
      {/* span wrapper keeps the tooltip working on disabled/read-only tiles */}
      <span className="inline-flex">
        {readOnly ? (
          <span
            role="img"
            aria-label={tooltip}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${TONES[tone]} ${className}`}
          >
            <Icon sx={{ fontSize: 20 }} />
          </span>
        ) : (
          <button
            type="button"
            aria-label={tooltip}
            onClick={inactive ? undefined : onClick}
            disabled={inactive}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nbl-accent ${
              disabled ? `${DISABLED} cursor-not-allowed shadow-none` : TONES[tone]
            } ${loading ? "cursor-wait" : ""} ${className}`}
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Icon sx={{ fontSize: 20 }} />
            )}
          </button>
        )}
      </span>
    </Tooltip>
  );
};

export default ViewerActionButton;
